import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  getNotificationSettings,
  updateNotificationSettings,
  getNotificationTemplates,
  updateNotificationTemplate,
  getNotificationLogs,
  retryNotification,
  testNotification
} from "../lib/api";
import { 
  MessageSquare, Settings, FileText, RotateCcw, Send, 
  CheckCircle2, XCircle, Clock, Loader2, Smartphone, Phone
} from "lucide-react";

const NotificationSettings = () => {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");
  
  // Template editing
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({
    template: "", enabled: true
  });
  
  // Test notification
  const [testPhone, setTestPhone] = useState("");
  const [testChannel, setTestChannel] = useState("whatsapp");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, templatesRes, logsRes] = await Promise.all([
        getNotificationSettings(),
        getNotificationTemplates(),
        getNotificationLogs(100)
      ]);
      setSettings(settingsRes.data);
      setTemplates(templatesRes.data);
      setLogs(logsRes.data);
    } catch (error) {
      toast.error("Failed to load notification data");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateNotificationSettings(settings);
      toast.success("Notification settings saved!");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      await updateNotificationTemplate(editingTemplate.template_id, templateFormData);
      toast.success("Template updated!");
      setEditingTemplate(null);
      loadData();
    } catch (error) {
      toast.error("Failed to update template");
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = async (logId) => {
    try {
      const result = await retryNotification(logId);
      if (result.data.success) {
        toast.success("Notification resent successfully!");
      } else {
        toast.error(result.data.reason || "Retry failed");
      }
      loadData();
    } catch (error) {
      toast.error("Retry failed");
    }
  };

  const handleTestNotification = async () => {
    if (!testPhone.trim()) {
      toast.error("Enter a phone number");
      return;
    }
    setTesting(true);
    try {
      const result = await testNotification(testPhone, testChannel);
      if (result.data.success) {
        toast.success(`Test ${testChannel.toUpperCase()} sent successfully!`);
      } else {
        toast.error(result.data.error || "Test failed");
      }
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateFormData({
      template: template.template || "",
      enabled: template.enabled !== false
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-amber-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-settings">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="settings">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="templates">
            <FileText className="h-4 w-4 mr-2" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="logs">
            <MessageSquare className="h-4 w-4 mr-2" />
            Logs
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Twilio Configuration</CardTitle>
              <CardDescription>
                Configure your Twilio credentials for WhatsApp and SMS notifications.
                Get your credentials from <a href="https://www.twilio.com/console" target="_blank" rel="noreferrer" className="text-blue-600 underline">Twilio Console</a>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Master Enable */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable Notifications</Label>
                  <p className="text-sm text-slate-500">Master switch for all notifications</p>
                </div>
                <Switch
                  checked={settings?.enabled || false}
                  onCheckedChange={(checked) => setSettings({...settings, enabled: checked})}
                  data-testid="enable-notifications-switch"
                />
              </div>

              {/* Twilio Credentials */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Twilio Account SID</Label>
                  <Input
                    value={settings?.twilio_account_sid || ""}
                    onChange={(e) => setSettings({...settings, twilio_account_sid: e.target.value})}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    data-testid="account-sid-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Twilio Auth Token</Label>
                  <Input
                    type="password"
                    value={settings?.twilio_auth_token || ""}
                    onChange={(e) => setSettings({...settings, twilio_auth_token: e.target.value})}
                    placeholder="Enter auth token"
                    data-testid="auth-token-input"
                  />
                </div>
              </div>

              {/* Channel Settings */}
              <div className="grid grid-cols-2 gap-6">
                {/* WhatsApp */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Smartphone className="h-5 w-5 text-emerald-600" />
                      <Label className="font-medium">WhatsApp</Label>
                    </div>
                    <Switch
                      checked={settings?.whatsapp_enabled || false}
                      onCheckedChange={(checked) => setSettings({...settings, whatsapp_enabled: checked})}
                      data-testid="whatsapp-enabled-switch"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">WhatsApp Number</Label>
                    <Input
                      value={settings?.twilio_whatsapp_number || ""}
                      onChange={(e) => setSettings({...settings, twilio_whatsapp_number: e.target.value})}
                      placeholder="+14155238886"
                    />
                    <p className="text-xs text-slate-500">Include country code with +</p>
                  </div>
                </div>

                {/* SMS */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Phone className="h-5 w-5 text-blue-600" />
                      <Label className="font-medium">SMS</Label>
                    </div>
                    <Switch
                      checked={settings?.sms_enabled || false}
                      onCheckedChange={(checked) => setSettings({...settings, sms_enabled: checked})}
                      data-testid="sms-enabled-switch"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">SMS Number</Label>
                    <Input
                      value={settings?.twilio_sms_number || ""}
                      onChange={(e) => setSettings({...settings, twilio_sms_number: e.target.value})}
                      placeholder="+1234567890"
                    />
                    <p className="text-xs text-slate-500">Your Twilio phone number</p>
                  </div>
                </div>
              </div>


              <Button onClick={handleSaveSettings} disabled={saving} className="w-full" data-testid="save-settings-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Settings
              </Button>
            </CardContent>
          </Card>

          {/* Test Notification */}
          <Card>
            <CardHeader>
              <CardTitle>Test Notification</CardTitle>
              <CardDescription>Send a test message to verify your configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Input
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="Phone number (e.g., +919876543210)"
                  className="flex-1"
                  data-testid="test-phone-input"
                />
                <select
                  value={testChannel}
                  onChange={(e) => setTestChannel(e.target.value)}
                  className="px-3 py-2 border rounded-md"
                >
                  <option value="whatsapp">WhatsApp</option>
                  <option value="sms">SMS</option>
                </select>
                <Button onClick={handleTestNotification} disabled={testing} data-testid="send-test-btn">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  <span className="ml-2">Send Test</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Notification Templates</CardTitle>
              <CardDescription>
                Customize the messages sent at each stage. Use variables like {"{request_number}"}, {"{pass_type}"}, etc.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Event</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Enabled</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium text-sm">{template.name}</TableCell>
                      <TableCell>
                        <span className="text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded">{template.recipients_info || "-"}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={template.enabled ? "default" : "secondary"}>
                          {template.enabled ? "Active" : "Disabled"}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[300px] truncate text-sm text-slate-500">
                        {template.template}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditTemplate(template)}
                          data-testid={`edit-template-${template.template_id}`}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Notification Logs</CardTitle>
              <CardDescription>Recent notification delivery status</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request #</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                        No notification logs yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-mono text-sm">{log.request_number}</TableCell>
                        <TableCell className="capitalize">{log.recipient_type}</TableCell>
                        <TableCell className="font-mono text-sm">{log.phone_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {log.channel || "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getStatusIcon(log.status)}
                            <span className="capitalize">{log.status}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {log.created_at ? new Date(log.created_at).toLocaleString("en-IN") : "-"}
                        </TableCell>
                        <TableCell>
                          {log.status === "failed" && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleRetry(log.id)}
                              data-testid={`retry-${log.id}`}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Template Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Template: {editingTemplate?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <p className="font-medium text-blue-800 mb-1">Recipients: <span className="text-blue-600">{editingTemplate?.recipients_info}</span></p>
              <p className="font-medium text-blue-800 mb-2">Available Variables:</p>
              <p className="text-blue-600 font-mono text-xs">
                {"{request_number}"} {"{pass_type}"} {"{exit_date}"} {"{exit_time}"} {"{expected_return_date}"} {"{expected_return_time}"} {"{reason}"} {"{destination}"} {"{student_name}"} {"{course_year}"} {"{action_datetime}"} {"{return_date}"} {"{return_time}"}
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label>Template Enabled</Label>
              <Switch
                checked={templateFormData.enabled}
                onCheckedChange={(checked) => setTemplateFormData({...templateFormData, enabled: checked})}
              />
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={templateFormData.template}
                onChange={(e) => setTemplateFormData({...templateFormData, template: e.target.value})}
                placeholder="Notification message"
                rows={6}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationSettings;
